// src/components/tariffs/TariffCalculator.tsx
import React, { useState, useEffect, useRef } from "react";
import { FileText, DollarSign, Grid3x3, ChevronDown } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { apiService } from "../../services/api";
import { getUser } from "../../utils/session";

/** Worldlink tokens */
const WL_RED = "#EF2D3E";
const WL_RED_DARK = "#d42635";


/** Shape of calculate_duty return dict */
interface TariffResult {
  product_name?: string | null;
  hts_no: string;
  country_of_origin: string;
  import_cost: number;
  import_quantity: number;

  base_duty_amount: number;
  additional_duty_amount: number;
  total_duty_amount: number;

  effective_duty_rate_percent: number | null;
  landed_cost: number;

  program_summary: string | null;
  notes: string | null;
}

interface TariffInputRecord {
  product_name: string;
  country_of_origin?: string | null;
  hts_no?: string | null;
  supplier_name?: string | null;
  supplier_product_cost?: number | string | null;
  [key: string]: unknown;
}

/** Payload expected by /save_tariff_calculation */
type TariffSavePayload = {
  product_name?: string;
  hts_code: string;
  country_of_origin: string;
  import_value: number;
  import_quantity: number;
  total_duty_amount: number;
  result: any;
};

type TariffFormData = {
  productName: string;
  htsCode: string;
  coo: string;
  supplierName: string;
  quantity: string;
  value: string;
};

type DerivedTariffData = {
  supplierOptions: string[];
  visibleHtsOptions: string[];
  countryOptions: string[];
  productLevelHtsOptions: string[];
  selectedSupplierName: string;
  matchedRecord: TariffInputRecord | null;
  totalImportCost: number;
};

const EMPTY_FORM_DATA: TariffFormData = {
  productName: "",
  htsCode: "",
  coo: "",
  supplierName: "",
  quantity: "",
  value: "",
};

/** Nicely format the program_summary JSON string */
const formatProgramSummary = (value: string | null): string => {
  if (!value) return "—";
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) return "—";
      return parsed.join(", ");
    }
  } catch {
    return value.replaceAll(/[[\]"]/g, "") || "—";
  }
  return String(value);
};

const formatCurrency = (value: number): string =>
  `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const formatPercent = (value: number): string =>
  `${Number.parseFloat(value.toFixed(1))}%`;

interface BreakdownRow {
  code: string;
  description: string;
  rateText: string;
}

const PERCENT_COMPONENT_PATTERN = /^-?\d+(?:\.\d+)?%$/;

const getProgramEntries = (value: string | null): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(isNonEmptyString) : [];
  } catch {
    return [];
  }
};

const parseProgramSummaryEntry = (entry: string): Pick<BreakdownRow, "code" | "rateText"> | null => {
  const separatorIndex = entry.indexOf(":");
  if (separatorIndex <= 0) return null;

  const code = entry.slice(0, separatorIndex).trim();
  const rateText = entry.slice(separatorIndex + 1).trim();
  if (!code || !rateText) return null;

  return { code, rateText };
};

const splitRateComponents = (rateText: string): string[] =>
  rateText
    .split("+")
    .map((component) => component.trim())
    .filter(Boolean);

const getBreakdownRows = (result: TariffResult): BreakdownRow[] => {
  const rows = getProgramEntries(result.program_summary)
    .map((entry, index) => {
      const parsedEntry = parseProgramSummaryEntry(entry);
      if (!parsedEntry) return null;

      return {
        code: parsedEntry.code,
        description: index === 0 ? result.product_name?.trim() || "Primary tariff" : "Additional tariff",
        rateText: parsedEntry.rateText,
      };
    })
    .filter((row): row is BreakdownRow => Boolean(row));

  if (rows.length > 0) return rows;

  return [
    {
      code: result.hts_no,
      description: result.product_name || formatProgramSummary(result.program_summary),
      rateText: formatPercent(result.effective_duty_rate_percent ?? 0),
    },
  ];
};

const getTotalRateDisplay = (
  tableRows: BreakdownRow[],
  fallbackRatePercent: number | null | undefined
): string => {
  const components = tableRows.flatMap((row) => splitRateComponents(row.rateText));

  if (components.length === 0) {
    return formatPercent(fallbackRatePercent ?? 0);
  }

  const uniqueComponents = Array.from(new Set(components));
  const percentComponents = uniqueComponents.filter((component) => PERCENT_COMPONENT_PATTERN.test(component));
  const nonPercentComponents = uniqueComponents.filter((component) => !PERCENT_COMPONENT_PATTERN.test(component));
  const nonZeroPercentComponents = percentComponents.filter((component) => component !== "0%");
  const normalizedPercentComponents =
    nonZeroPercentComponents.length > 0 ? nonZeroPercentComponents : percentComponents;

  return [...normalizedPercentComponents, ...nonPercentComponents].join(" + ");
};

const normalizeCountry = (value: string): string => {
  return value.trim();
};

const parseNumeric = (value?: string | number | null): number => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sortStrings = (values: string[]): string[] =>
  [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

const uniqueNonEmptyStrings = (values: Array<string | null | undefined>): string[] =>
  sortStrings(
    Array.from(new Set(values.filter((value): value is string => Boolean(value && value.trim() !== ""))))
  );

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "";

const getArrayValues = <T,>(
  value: unknown,
  predicate: (entry: unknown) => entry is T
): T[] => (Array.isArray(value) ? value.filter(predicate) : []);

const getStringArrayValues = (value: unknown): string[] => getArrayValues(value, isNonEmptyString);

const getValidTariffRecords = (value: unknown): TariffInputRecord[] =>
  getArrayValues(
    value,
    (row: unknown): row is TariffInputRecord =>
      Boolean(row && typeof row === "object" && isNonEmptyString((row as TariffInputRecord).product_name))
  );

const getDerivedOptions = (
  records: TariffInputRecord[],
  fallbackValue: unknown,
  recordSelector: (record: TariffInputRecord) => string | null | undefined
): string[] => {
  const recordValues = uniqueNonEmptyStrings(records.map(recordSelector));
  if (recordValues.length > 0) return recordValues;
  return uniqueNonEmptyStrings(getStringArrayValues(fallbackValue));
};

const matchRecord = (
  record: TariffInputRecord,
  filters: Partial<Pick<TariffFormData, "productName" | "coo" | "supplierName">>
): boolean => {
  if (filters.productName && record.product_name !== filters.productName) return false;
  if (filters.coo && record.country_of_origin !== filters.coo) return false;
  if (filters.supplierName && (record.supplier_name || "") !== filters.supplierName) return false;
  return true;
};

const withCurrentValue = (options: string[], currentValue: string): string[] => {
  if (!currentValue || options.includes(currentValue)) return options;
  return sortStrings([...options, currentValue]);
};

const deriveTariffData = (
  inputRecords: TariffInputRecord[],
  countryCodes: string[],
  htsCodes: string[],
  formData: TariffFormData
): DerivedTariffData => {
  const hasSelectedProduct = Boolean(formData.productName.trim());
  const filteredRecords = hasSelectedProduct
    ? inputRecords.filter((record) =>
        matchRecord(record, {
          productName: formData.productName,
          coo: formData.coo,
          supplierName: formData.supplierName,
        })
      )
    : [];

  const supplierSourceRecords = hasSelectedProduct
    ? inputRecords.filter((record) =>
        matchRecord(record, {
          productName: formData.productName,
          coo: formData.coo,
        })
      )
    : [];

  const productRecords = formData.productName
    ? inputRecords.filter((record) => record.product_name === formData.productName)
    : [];

  const recordCountryOptions = formData.productName
    ? uniqueNonEmptyStrings(productRecords.map((record) => record.country_of_origin))
    : [];
  const supplierOptions = uniqueNonEmptyStrings(
    supplierSourceRecords.map((record) => record.supplier_name || "")
  );
  const htsOptions = uniqueNonEmptyStrings(filteredRecords.map((record) => record.hts_no || ""));
  const productLevelHtsOptions = uniqueNonEmptyStrings(productRecords.map((record) => record.hts_no || ""));

  const selectedSupplierName =
    formData.supplierName || (supplierOptions.length === 1 ? supplierOptions[0] : "");
  const matchedRecord =
    filteredRecords.find((record) =>
      matchRecord(record, {
        productName: formData.productName,
        coo: formData.coo,
        supplierName: selectedSupplierName,
      })
    ) || null;

  const unitCost = parseNumeric(matchedRecord?.supplier_product_cost);
  const quantityNumber = parseNumeric(formData.quantity);
  const defaultQuantity = matchedRecord ? 1 : 0;
  const effectiveQuantity = quantityNumber > 0 ? quantityNumber : defaultQuantity;
  const totalImportCost = unitCost * effectiveQuantity;

  return {
    supplierOptions,
    visibleHtsOptions: withCurrentValue(htsOptions.length > 0 ? htsOptions : htsCodes, formData.htsCode),
    countryOptions: withCurrentValue(
      recordCountryOptions.length > 0 ? recordCountryOptions : countryCodes,
      formData.coo
    ),
    productLevelHtsOptions,
    selectedSupplierName,
    matchedRecord,
    totalImportCost,
  };
};

const getFormDataWithDependencies = (
  currentFormData: TariffFormData,
  field: keyof TariffFormData,
  value: string,
  useDerivedInputs: boolean
): TariffFormData => {
  const next = { ...currentFormData, [field]: value };
  if (!useDerivedInputs) return next;

  if (field === "productName") {
    next.supplierName = "";
    next.htsCode = "";
    next.coo = "";
    next.value = "";
  }

  if (field === "coo") {
    next.supplierName = "";
    next.htsCode = "";
    next.value = "";
  }

  if (field === "supplierName") {
    next.htsCode = "";
    next.value = "";
  }

  return next;
};

const getSyncedFormData = (
  formData: TariffFormData,
  inputRecordCount: number,
  matchedRecord: TariffInputRecord | null,
  productLevelHtsOptions: string[],
  selectedSupplierName: string,
  totalImportCost: number
): TariffFormData | null => {
  if (!matchedRecord) {
    const autoHtsCode =
      inputRecordCount > 0 && formData.productName && productLevelHtsOptions.length === 1
        ? productLevelHtsOptions[0]
        : "";
    if (
      autoHtsCode !== "" &&
      formData.htsCode !== autoHtsCode &&
      (!formData.coo || !selectedSupplierName)
    ) return { ...formData, htsCode: autoHtsCode };
    return formData.productName && formData.coo && inputRecordCount > 0 && (formData.value || formData.quantity)
      ? { ...formData, quantity: "", value: "" }
      : null;
  }

  const nextHtsCode = matchedRecord.hts_no || "";
  const nextValue =
    formData.productName && formData.coo && selectedSupplierName && nextHtsCode && totalImportCost > 0
      ? totalImportCost.toFixed(2)
      : "";
  return formData.htsCode === nextHtsCode && formData.value === nextValue
    ? null
    : { ...formData, htsCode: nextHtsCode, value: nextValue };
};

const getUnitLabel = (unit: unknown): string => {
  if (!unit || typeof unit !== "string" || unit.trim() === "") return "(units)";
  const map: Record<string, string> = { t: "TONS", kg: "KGS", lbs: "LBS" };
  return map[unit.toLowerCase()] ?? unit.toUpperCase();
};

const getCalculationError = (
  formData: TariffFormData,
  supplierOptions: string[],
  selectedSupplierName: string
): string | null => {
  if (!formData.productName.trim() && !formData.htsCode.trim()) {
    return "Please enter a product name or HTS code.";
  }
  if (!formData.coo) {
    return "Please select a country of origin.";
  }
  if (supplierOptions.length > 1 && !selectedSupplierName) {
    return "Please select a supplier.";
  }
  return null;
};

const FIELD_STYLE: React.CSSProperties = {
  width: "100%",
  borderRadius: 8,
  border: "1px solid #EAEAEA",
  background: "#fff",
  padding: "0 12px",
  height: 40,
  fontSize: "1rem",
  color: "#111111",
  outline: "none",
  boxSizing: "border-box",
};

const DROPDOWN_LIST_STYLE: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  right: 0,
  background: "#fff",
  border: "1px solid #EAEAEA",
  borderRadius: 8,
  boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
  zIndex: 1000,
  maxHeight: 220,
  overflowY: "auto",
};

interface DropdownFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  showClearOption?: boolean;
  disabled?: boolean;
  id?: string;
  itemFontSize?: string;
}

const DropdownField: React.FC<DropdownFieldProps> = ({
  value,
  onChange,
  options,
  placeholder,
  showClearOption = false,
  disabled = false,
  id,
  itemFontSize = "0.875rem",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleSelect = (selected: string) => {
    onChange(selected);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <button
        type="button"
        id={id}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        style={{
          ...FIELD_STYLE,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "default" : "pointer",
          textAlign: "left",
          border: isOpen ? "1px solid #EF2D3E" : "1px solid #EAEAEA",
          boxShadow: isOpen ? "0 0 0 2px #EF2D3E, 0 0 0 5px rgba(239,45,62,0.12)" : "none",
        }}
      >
        <span style={{ color: value ? "#111111" : "#9CA3AF", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value || placeholder}
        </span>
        {!disabled && (
          <ChevronDown size={16} style={{ color: "#6B7280", flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
        )}
      </button>
      {isOpen && (
        <div style={DROPDOWN_LIST_STYLE}>
          {showClearOption && (
            <button
              type="button"
              onClick={() => handleSelect("")}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", fontSize: "0.875rem", cursor: "pointer", color: "#9CA3AF", borderBottom: "1px solid #EAEAEA", fontStyle: "italic", background: "transparent", border: "none" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#F7F7F7"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            >
              None (clear)
            </button>
          )}
          {options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleSelect(option)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", fontSize: itemFontSize, cursor: "pointer", background: value === option ? "#374151" : "transparent", color: value === option ? "#fff" : "#111111", border: "none" }}
              onMouseEnter={(e) => { if (value !== option) (e.currentTarget as HTMLButtonElement).style.background = "#F7F7F7"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = value === option ? "#374151" : "transparent"; }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface ComboboxFieldProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  showClearOption?: boolean;
  id?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const ComboboxField: React.FC<ComboboxFieldProps> = ({
  value,
  onChange,
  options,
  placeholder,
  showClearOption = false,
  id,
  onKeyDown,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [filterText, setFilterText] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setInputValue(value); }, [value]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setFilterText("");
        setInputValue(value);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [value]);

  const filtered = filterText
    ? options.filter((o) => o.toLowerCase().includes(filterText.toLowerCase()))
    : options;

  const handleOpen = () => {
    setFilterText("");
    setIsOpen(true);
  };

  const handleType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setFilterText(e.target.value);
    onChange(e.target.value);
    setIsOpen(true);
  };

  const handleSelect = (selected: string) => {
    setInputValue(selected);
    setFilterText("");
    onChange(selected);
    setIsOpen(false);
  };

  const handleClear = () => {
    setInputValue("");
    setFilterText("");
    onChange("");
    setIsOpen(false);
  };

  const showList = isOpen && (filtered.length > 0 || showClearOption);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <input
          id={id}
          type="text"
          value={inputValue}
          placeholder={placeholder}
          onChange={handleType}
          onFocus={handleOpen}
          onClick={handleOpen}
          onKeyDown={onKeyDown}
          style={{
            ...FIELD_STYLE,
            paddingRight: 32,
            color: inputValue ? "#111111" : "#9CA3AF",
          }}
        />
        <ChevronDown
          size={16}
          onClick={() => isOpen ? setIsOpen(false) : handleOpen()}
          style={{ position: "absolute", right: 10, color: "#6B7280", cursor: "pointer", flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        />
      </div>
      {showList && (
        <div style={{ ...DROPDOWN_LIST_STYLE, maxHeight: 220, overflowY: "auto" }}>
          {showClearOption && (
            <button
              type="button"
              onClick={handleClear}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", fontSize: "0.875rem", cursor: "pointer", color: "#9CA3AF", borderBottom: "1px solid #EAEAEA", fontStyle: "italic", background: "transparent", border: "none" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#F7F7F7"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            >
              None (clear)
            </button>
          )}
          {filtered.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleSelect(option)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 12px", fontSize: "0.875rem", cursor: "pointer", background: "transparent", color: "#111111", border: "none" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#F7F7F7"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "transparent"; }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const getCalcButtonBackground = (isDisabled: boolean, isHovered: boolean) => {
  if (isDisabled) return "#9CA3AF";
  return isHovered ? WL_RED_DARK : WL_RED;
};

const getCalcButtonStyle = (isDisabled: boolean, isHovered: boolean): React.CSSProperties => ({
  width: "100%", marginTop: 8, height: 44, borderRadius: 8,
  background: getCalcButtonBackground(isDisabled, isHovered),
  color: "#fff", border: "none", fontSize: "1rem", fontWeight: 600,
  cursor: isDisabled ? "not-allowed" : "pointer",
  transition: "background 0.15s",
  opacity: isDisabled ? 0.6 : 1,
});

const getSaveLabel = (isLoading: boolean) => isLoading ? "Saving..." : "Save Calculation";

const getCalcLabel = (isLoading: boolean, isRecalculate: boolean) =>
  isLoading ? "Calculating..." : getCalcActionLabel(isRecalculate);

const getCalcActionLabel = (isRecalculate: boolean) => isRecalculate ? "Recalculate" : "Calculate Tariff";

const getSaveButtonBackground = (isSaved: boolean, isHovered: boolean, isLoading: boolean) => {
  if (isSaved) return "#10B981";
  return isHovered && !isLoading ? WL_RED_DARK : WL_RED;
};

const getSaveButtonStyle = (isSaved: boolean, isHovered: boolean, isLoading: boolean): React.CSSProperties => ({
  width: "100%", height: 44, borderRadius: 8, border: "none",
  background: getSaveButtonBackground(isSaved, isHovered, isLoading),
  color: "#fff", fontSize: "1rem", fontWeight: 600,
  cursor: isSaved || isLoading ? "not-allowed" : "pointer",
  transition: "background 0.15s",
  opacity: isLoading ? 0.6 : 1,
});

interface TariffCalculatorProps {
  onCalculationComplete?: () => void;
  selectedCalculation?: any;
  onNewCalculation?: () => void;
}

const TariffCalculator: React.FC<TariffCalculatorProps> = ({
  onCalculationComplete,
  selectedCalculation,
  onNewCalculation,
}) => {
  const { user: clerkUser } = useUser();

  const [formData, setFormData] = useState(EMPTY_FORM_DATA);
  const [result, setResult] = useState<TariffResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [inputRecords, setInputRecords] = useState<TariffInputRecord[]>([]);
  const [productNames, setProductNames] = useState<string[]>([]);
  const [countryCodes, setCountryCodes] = useState<string[]>([]);
  const [htsCodes, setHtsCodes] = useState<string[]>([]);
  const [currentCalculation, setCurrentCalculation] = useState<TariffSavePayload | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [calcHovered, setCalcHovered] = useState(false);
  const [saveHovered, setSaveHovered] = useState(false);

  useEffect(() => {
    if (!selectedCalculation) return;

    let supplier = "";
    if (inputRecords.length > 0) {
      const matches = inputRecords.filter(r => 
        r.product_name === selectedCalculation.product_name && 
        r.country_of_origin === selectedCalculation.country_of_origin
      );
      const uniqueSuppliers = Array.from(new Set(matches.map(m => m.supplier_name).filter(Boolean)));
      if (uniqueSuppliers.length === 1) {
        supplier = uniqueSuppliers[0] as string;
      } else if (uniqueSuppliers.length > 1) {
        // Try to find the supplier by matching the import value
        const importQty = selectedCalculation.import_quantity || 1;
        const importVal = selectedCalculation.import_value || 0;
        const matchedByCost = matches.find(m => {
          const cost = parseNumeric(m.supplier_product_cost);
          return Math.abs(cost * importQty - importVal) < 0.01;
        });
        if (matchedByCost && matchedByCost.supplier_name) {
          supplier = matchedByCost.supplier_name;
        }
      }
    }

    setFormData({
      productName: selectedCalculation.product_name || "",
      htsCode: selectedCalculation.hts_code || "",
      coo: selectedCalculation.country_of_origin || "",
      supplierName: supplier,
      quantity: String(selectedCalculation.import_quantity || ""),
      value: String(selectedCalculation.import_value || ""),
    });
    if (selectedCalculation.result) {
      let parsedResult = selectedCalculation.result;
      if (typeof parsedResult === "string") {
        try { parsedResult = JSON.parse(parsedResult); } catch { /* leave as-is */ }
      }
      setResult(parsedResult as TariffResult);
    }
    setError(null);
    setCurrentCalculation(null);
    setIsSaved(false);
    setSaveError(null);
    setSaveLoading(false);
  }, [selectedCalculation, inputRecords]);

  useEffect(() => {
    let isMounted = true;
    const loadInputs = async () => {
      try {
        const response = await apiService.getTariffInputs();
        const data = response?.data || {};
        const validRecords = getValidTariffRecords(data.records);
        const nextProductNames = getDerivedOptions(validRecords, data.product_names, (record) => record.product_name);
        const nextCountryCodes = getDerivedOptions(validRecords, data.country_codes, (record) => record.country_of_origin);
        const nextHtsCodes = getDerivedOptions(validRecords, data.hts_codes, (record) => record.hts_no);
        if (!isMounted) return;
        setInputRecords(validRecords);
        setProductNames(nextProductNames);
        setCountryCodes(nextCountryCodes);
        setHtsCodes(nextHtsCodes);
      } catch (err) {
        console.error("Failed to load tariff inputs:", err);
        if (!isMounted) return;
        setInputRecords([]);
        setCountryCodes([]);
        setProductNames([]);
        setHtsCodes([]);
      }
    };
    loadInputs();
    return () => { isMounted = false; };
  }, []);

  const handleInputChange = (field: keyof TariffFormData, value: string) => {
    if (selectedCalculation) {
      onNewCalculation?.();
    }
    setFormData((prev) => getFormDataWithDependencies(prev, field, value, inputRecords.length > 0));
    setResult(null);
    setCurrentCalculation(null);
    setIsSaved(false);
    setSaveError(null);
  };

  const {
    supplierOptions,
    visibleHtsOptions,
    countryOptions,
    productLevelHtsOptions,
    selectedSupplierName,
    matchedRecord,
    totalImportCost,
  } = deriveTariffData(inputRecords, countryCodes, htsCodes, formData);

  const unitCost = parseNumeric(matchedRecord?.supplier_product_cost);
  const quantityNumber = parseNumeric(formData.quantity);

  useEffect(() => {
    if (selectedCalculation) return;
    const nextFormData = getSyncedFormData(
      formData, inputRecords.length, matchedRecord,
      productLevelHtsOptions, selectedSupplierName, totalImportCost
    );
    if (nextFormData) setFormData(nextFormData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedRecord, quantityNumber, totalImportCost, unitCost, formData.productName, formData.coo, inputRecords.length, productLevelHtsOptions, selectedSupplierName, selectedCalculation]);

  const calculateTariff = async () => {
    const calculationError = getCalculationError(formData, supplierOptions, selectedSupplierName);
    if (calculationError) { setError(calculationError); return; }
    setError(null); setResult(null); setLoading(true);
    setCurrentCalculation(null); setIsSaved(false); setSaveError(null);
    try {
      const normalizedCoo = normalizeCountry(formData.coo);
      const importValue = formData.value ? Number(formData.value) : 0;
      const defaultQuantity = matchedRecord ? 1 : 0;
      const importQuantity = formData.quantity ? Number(formData.quantity) : defaultQuantity;
      const userId = clerkUser?.id || getUser()?.email || "anonymous";
      const response = await apiService.calculateTariff({
        product_name: formData.productName.trim() || undefined,
        hts_code: formData.htsCode.trim() || undefined,
        country_of_origin: normalizedCoo,
        value: importValue,
        quantity: importQuantity,
        user_id: userId,
      });
      const data = response.data;
      if (!data || typeof data !== "object" || Object.keys(data).length === 0) {
        setError("No tariff data found for the given HTS code and country of origin.");
        return;
      }
      setResult(data as TariffResult);
      setCurrentCalculation({
        product_name: formData.productName.trim() || (data as any)?.product_name || undefined,
        hts_code: formData.htsCode.trim() || (data as any)?.hts_no || "",
        country_of_origin: normalizedCoo,
        import_value: importValue,
        import_quantity: importQuantity,
        total_duty_amount: (data as any)?.total_duty_amount ?? 0,
        result: data,
      });
      setIsSaved(false);
    } catch (err) {
      console.error("Error calculating tariff:", err);
      setError("Unable to calculate tariff. Please verify your inputs.");
    } finally {
      setLoading(false);
    }
  };

  const saveCalculation = async () => {
    if (!currentCalculation) return;
    setSaveLoading(true); setSaveError(null);
    try {
      await apiService.saveTariffCalculation(currentCalculation);
      setIsSaved(true);
      onCalculationComplete?.();
    } catch (err) {
      console.error("Error saving calculation:", err);
      setSaveError("Failed to save calculation. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleNewCalculation = () => {
    setFormData(EMPTY_FORM_DATA);
    setResult(null); setError(null);
    setCurrentCalculation(null); setIsSaved(false);
    setSaveError(null); setSaveLoading(false);
    onNewCalculation?.();
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.border = "1px solid #EF2D3E";
    e.currentTarget.style.boxShadow = "0 0 0 2px #EF2D3E, 0 0 0 5px rgba(239, 45, 62, 0.12)";
  };
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.border = "1px solid #EAEAEA";
    e.currentTarget.style.boxShadow = "none";
  };

  const blockNonNumeric = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
  };

  const blockNonInteger = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
  };

  const blockNonHts = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/^[\d.]$/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const isCalcDisabled = (!formData.htsCode && !formData.productName) || !formData.coo || loading;

  return (
    <div style={{ display: "flex", flexDirection: "row", gap: 20, alignItems: "stretch", flexWrap: "wrap", fontFamily: "'Inter', sans-serif" }}>
      {/* ── Left panel: Input Parameters ── */}
      <div style={{ flex: "1 1 0", minWidth: 300, borderRadius: 8, border: "1px solid #EAEAEA", background: "#fff", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8, background: "#EFF6FF", flexShrink: 0 }}>
              <FileText size={16} style={{ color: "#3B82F6" }} />
            </div>
            <span style={{ fontSize: "1rem", fontWeight: 600, color: "#111111" }}>Input Parameters</span>
          </div>
          {(selectedCalculation || Object.values(formData).some(Boolean)) && (
            <button
              onClick={handleNewCalculation}
              title={selectedCalculation ? "New calculation" : "Clear inputs"}
              aria-label={selectedCalculation ? "New calculation" : "Clear inputs"}
              style={{
                background: "none",
                border: "1px solid #E5E7EB",
                borderRadius: 8,
                cursor: "pointer",
                padding: "6px 8px",
                display: "flex",
                alignItems: "center",
                color: "#6B7280",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label htmlFor="product-select" style={{ display: "block", marginBottom: 6, fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
              Product Name <span style={{ color: "#EF2D3E" }}>*</span>
            </label>
            <ComboboxField
              id="product-select"
              value={formData.productName}
              onChange={(v) => handleInputChange("productName", v)}
              options={productNames}
              placeholder="Search or enter product..."
            />
          </div>

          <div>
            <label htmlFor="hts-select" style={{ display: "block", marginBottom: 6, fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
              HTS Code <span style={{ fontWeight: 400, color: "#6B7280" }}>(optional)</span>
            </label>
            <ComboboxField
              id="hts-select"
              value={formData.htsCode}
              onChange={(v) => handleInputChange("htsCode", v)}
              options={visibleHtsOptions}
              placeholder="Search or enter HTS code..."
              showClearOption
              onKeyDown={blockNonHts}
            />
          </div>

          <div>
            <label htmlFor="coo-select" style={{ display: "block", marginBottom: 6, fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
              Country <span style={{ color: "#EF2D3E" }}>*</span>
            </label>
            <DropdownField
              id="coo-select"
              value={formData.coo}
              onChange={(v) => handleInputChange("coo", v)}
              options={countryOptions}
              placeholder="Select country..."
              itemFontSize="0.9375rem"
            />
          </div>

          <div>
            <label htmlFor="supplier-select" style={{ display: "block", marginBottom: 6, fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
              Supplier <span style={{ color: "#EF2D3E" }}>*</span>
            </label>
            <DropdownField
              id="supplier-select"
              value={formData.supplierName}
              onChange={(v) => handleInputChange("supplierName", v)}
              options={supplierOptions}
              placeholder="Select supplier..."
              showClearOption
              disabled={supplierOptions.length === 0}
            />
          </div>

          <div>
            <label htmlFor="base-cost" style={{ display: "block", marginBottom: 6, fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
              Base Cost ($) <span style={{ color: "#EF2D3E" }}>*</span>
            </label>
            <input id="base-cost" type="number" min="0" value={formData.value}
              onChange={(e) => handleInputChange("value", e.target.value)}
              onKeyDown={blockNonNumeric}
              onFocus={onFocus} onBlur={onBlur} style={FIELD_STYLE} />
          </div>

          <div>
            <label htmlFor="import-qty" style={{ display: "block", marginBottom: 6, fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
              Import Quantity <span style={{ color: "#EF2D3E" }}>*</span>
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input id="import-qty" type="number" min="0" value={formData.quantity}
                onChange={(e) => handleInputChange("quantity", e.target.value)}
                onKeyDown={blockNonInteger}
                onFocus={onFocus} onBlur={onBlur} style={{ ...FIELD_STYLE, flex: 1 }} />
              <span style={{ fontSize: "0.875rem", color: "#374151", whiteSpace: "nowrap", fontWeight: 500 }}>
                {getUnitLabel(matchedRecord?.base_specific_unit)}
              </span>
            </div>
          </div>

          <button
            onClick={calculateTariff}
            disabled={isCalcDisabled}
            onMouseEnter={() => setCalcHovered(true)}
            onMouseLeave={() => setCalcHovered(false)}
            style={getCalcButtonStyle(isCalcDisabled, calcHovered)}
          >
            {getCalcLabel(loading, Boolean(selectedCalculation))}
          </button>
        </div>
      </div>

      {/* ── Right panel: Cost Breakdown ── */}
      <div style={{ flex: "1 1 0", minWidth: 300, borderRadius: 8, border: "1px solid #EAEAEA", background: "#fff", padding: 20, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, background: "#F0FDF4", flexShrink: 0 }}>
            <DollarSign size={18} style={{ color: "#16A34A" }} />
          </div>
          <span style={{ fontSize: "1rem", fontWeight: 600, color: "#111111" }}>Cost Breakdown</span>
        </div>

        {error && <p style={{ color: "#B91C1C", fontSize: "0.875rem", marginBottom: 16 }}>{error}</p>}

        {result && !error ? (() => {
          const totalCost = result.import_cost;
          const tariffLevied = result.total_duty_amount;
          const costOfImport = result.landed_cost;
          const tableRows = getBreakdownRows(result);
          const totalRateDisplay = getTotalRateDisplay(tableRows, result.effective_duty_rate_percent);

          return (
            <div>
              {/* 3 KPI tiles */}
              <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
                {[
                  { label: "Total Cost", value: formatCurrency(totalCost) },
                  { label: "Tariff Levied", value: formatCurrency(tariffLevied) },
                  { label: "Cost of Import", value: formatCurrency(costOfImport) },
                ].map(({ label, value }) => (
                  <div key={label} style={{ flex: 1, border: "1px solid #E5E7EB", borderRadius: 18, padding: "18px 10px", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                    <p style={{ fontSize: "0.75rem", color: "#2563EB", marginBottom: 6 }}>{label}</p>
                    <p style={{ fontSize: "1.2rem", fontWeight: 700, color: "#111111" }}>{value}</p>
                  </div>
                ))}
              </div>

              {/* HTS breakdown table */}
              <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
                {/* Header */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr 0.6fr", background: "#F9FAFB", padding: "10px 16px", borderBottom: "1px solid #E5E7EB" }}>
                  <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#374151" }}>HTS Code</span>
                  <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#374151" }}>Description</span>
                  <span style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#374151", textAlign: "right" }}>Rate</span>
                </div>
                {tableRows.map((row, index) => (
                  <div
                    key={`${row.code}-${index}`}
                    style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr 0.6fr", padding: "12px 16px", borderBottom: "1px solid #F3F4F6", background: "#fff" }}
                  >
                    <span style={{ fontSize: "0.875rem", color: "#111111" }}>{row.code}</span>
                    <span style={{ fontSize: "0.875rem", color: "#111111" }}>{row.description}</span>
                    <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "#111111", textAlign: "right" }}>{row.rateText}</span>
                  </div>
                ))}
                {/* Total row */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.6fr 0.6fr", padding: "12px 16px", background: "#F9FAFB" }}>
                  <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111111" }}>Total</span>
                  <span />
                  <span style={{ fontSize: "0.875rem", fontWeight: 700, color: "#111111", textAlign: "right" }}>{totalRateDisplay}</span>
                </div>
              </div>

              {/* Notes */}
              {result.notes && (
                <div style={{ border: "1px solid #FDE68A", borderRadius: 16, padding: "14px 16px", marginBottom: 16, background: "#FFFBEB" }}>
                  <p style={{ fontSize: "0.875rem", fontWeight: 600, color: "#92400E", marginBottom: 6 }}>Notes</p>
                  <p style={{ fontSize: "0.8125rem", color: "#92400E", lineHeight: 1.6 }}>{result.notes}</p>
                </div>
              )}

              
            </div>
          );
        })() : (
          !error && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, textAlign: "center" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 64, height: 64, borderRadius: 8, background: "#F7F7F7", marginBottom: 16 }}>
                <Grid3x3 size={30} style={{ color: "#9CA3AF" }} />
              </div>
              <p style={{ fontSize: "1rem", fontWeight: 600, color: "#111111", marginBottom: 4 }}>No results yet</p>
              <p style={{ fontSize: "0.875rem", color: "#4B5563", maxWidth: 220 }}>
                Enter all required inputs and click to view cost breakdown
              </p>
            </div>
          )
        )}

        {currentCalculation && (
          <div style={{ marginTop: "auto", paddingTop: 12 }}>
            <button
              onClick={saveCalculation}
              disabled={isSaved || saveLoading}
              onMouseEnter={() => setSaveHovered(true)}
              onMouseLeave={() => setSaveHovered(false)}
              style={getSaveButtonStyle(isSaved, saveHovered, saveLoading)}
            >
              {isSaved ? "✓ Saved" : getSaveLabel(saveLoading)}
            </button>
            {saveError && <p style={{ marginTop: 8, fontSize: "0.875rem", color: "#B91C1C" }}>{saveError}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default TariffCalculator;

